import React from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import { useConfiguration } from '../../../../context/configurationContext';
import { ExternalLink, IconEmail, NamedLink } from '../../../../components';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;
const WEARBY_SOCIAL_HANDLE = 'wearbymarket';

const WEARBY_SOCIAL_MEDIA_LINKS = [
  {
    blockType: 'socialMediaLink',
    link: {
      fieldType: 'socialMediaLink',
      platform: 'twitter',
      url: `https://x.com/${WEARBY_SOCIAL_HANDLE}`,
    },
  },
  {
    blockType: 'socialMediaLink',
    link: {
      fieldType: 'socialMediaLink',
      platform: 'tiktok',
      url: `https://www.tiktok.com/@${WEARBY_SOCIAL_HANDLE}`,
    },
  },
];

/**
 * Merge hosted footer social links with Wearby's X and TikTok profiles.
 * Hosted links win if the same platform is already configured in Console.
 *
 * @param {Array<Object>} hostedLinks social media link configs from the footer asset
 * @returns {Array<Object>} hosted links plus any missing Wearby profiles
 */
const mergeWearbySocialMediaLinks = (hostedLinks = []) => {
  const existingPlatforms = new Set(
    hostedLinks.map(sml => sml?.link?.platform).filter(Boolean)
  );
  const extraLinks = WEARBY_SOCIAL_MEDIA_LINKS.filter(
    sml => !existingPlatforms.has(sml.link.platform)
  );
  return [...hostedLinks, ...extraLinks];
};

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.sectionId id of the section
 * @param {'footer'} props.sectionType
 * @param {number} props.numberOfColumns columns for blocks in footer (1-4)
 * @param {Array<SocialMediaLinkConfig>?} props.socialMediaLinks array of social media link configs
 * @param {Object?} props.slogan
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns = 1,
    socialMediaLinks = [],
    slogan,
    appearance,
    copyright,
    blocks = [],
    options,
    linkLogoToExternalSite,
  } = props;

  const intl = useIntl();
  const { siteContactEmail } = useConfiguration();

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const resolvedSocialMediaLinks = mergeWearbySocialMediaLinks(socialMediaLinks);
  const linksWithBlockId = resolvedSocialMediaLinks.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = resolvedSocialMediaLinks.length > 0;
  const showContactEmail = !!siteContactEmail;
  const showIcons = showContactEmail || showSocialMediaLinks;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';
  const emailAriaLabel = intl.formatMessage({ id: 'SectionFooter.emailLinkAriaLabel' });

  // use block builder instead of mapping blocks manually

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <div className={classNames(css.content, getContentCss(numberOfColumns))}>
          <div className={css.logo}>
            {/* <LinkedLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoWrapper}
              logoImageClassName={css.logoImage}
              linkToExternalSite={linkLogoToExternalSite}
              layout={logoLayout}
            /> */}
            <NamedLink
              name="LandingPage"
              className={css.logoLink}
              rootClassName={css.logoWrapper}
              logoImageClassName={css.logoImage}
              layout={logoLayout}
            >
              {/* <IconCollection name="logo" /> */}
              WEARBY
            </NamedLink>
            {showIcons ? (
              <div className={css.icons}>
                {showContactEmail ? (
                  <ExternalLink
                    className={css.emailLink}
                    href={`mailto:${siteContactEmail}`}
                    title={emailAriaLabel}
                    aria-label={emailAriaLabel}
                    target="_self"
                  >
                    <IconEmail className={css.emailIcon} />
                  </ExternalLink>
                ) : null}
                {showSocialMediaLinks ? (
                  <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
                ) : null}
              </div>
            ) : null}
          </div>
          <div className={css.sloganMobile}>
            <Field data={slogan} className={css.slogan} />
          </div>
          <div className={css.detailsInfo}>
            <div className={css.sloganDesktop}>
              <Field data={slogan} className={css.slogan} />
            </div>
          </div>
          <div className={classNames(css.grid, getGridCss(numberOfColumns))}>
            <BlockBuilder blocks={blocks} sectionId={sectionId} options={options} />
          </div>
        </div>
      </div>
      <div className={css.copyrightContainer}>
        <span className={css.copyrightText}>All rights reserved.</span>
        <Field data={copyright} className={css.copyright} />
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
